import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import { useSeo } from "@/lib/api";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import { CtaTrio } from "@/components/common/CtaButtons";
import Breadcrumb from "@/components/common/Breadcrumb";
import ProductCard from "@/components/common/ProductCard";
import FinalCta from "@/components/common/FinalCta";
import { getServicesByCategory } from "@/data/services";

// category: "particuliers" | "professionnels" | "epargne"
export default function CategoryHub({ category }) {
  const { t, lang } = useLang();
  const { openForm } = useConversion();
  const hub = t.hubs[category];
  const navMap = {
    particuliers: t.nav.particuliers,
    professionnels: t.nav.professionnels,
    epargne: t.nav.epargne,
  };
  const nav = navMap[category];

  const services =
    category === "epargne"
      ? getServicesByCategory("epargne")
      : getServicesByCategory(category);

  useSeo({
    lang,
    path: nav.path,
    title: `${nav.label} — NEKASSUR`,
    description: hub.intro,
  });

  return (
    <>
      <section className="bg-nek-navy">
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: nav.label }]} />
          <Reveal className="mt-8 max-w-3xl">
            <Eyebrow className="!text-nek-teal-100">{hub.eyebrow}</Eyebrow>
            <h1 className="nek-h1 mt-4 text-white" style={{ fontSize: "clamp(2.4rem,4.4vw,3.6rem)" }}>{hub.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">{hub.intro}</p>
            <div className="mt-8"><CtaTrio variant="dark" /></div>
          </Reveal>
        </div>
      </section>

      {/* Segments (professionnels) */}
      {category === "professionnels" && (
        <Section tone="white">
          <Reveal><h2 className="text-2xl font-heading font-bold text-nek-navy">{hub.segmentsTitle}</h2></Reveal>
          <div className="mt-6 flex flex-wrap gap-3">
            {hub.segments.map((s) => (
              <span key={s} className="rounded-full border border-nek-border bg-nek-light px-5 py-2.5 text-sm font-medium text-nek-navy">{s}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Complex highlight (particuliers) */}
      {category === "particuliers" && (
        <Section tone="white">
          <Reveal className="flex flex-col items-start justify-between gap-6 rounded-[24px] bg-nek-coral/5 p-8 sm:flex-row sm:items-center sm:p-10">
            <div>
              <h2 className="text-xl font-heading font-bold text-nek-navy sm:text-2xl">{hub.complexTitle}</h2>
              <p className="mt-2 max-w-xl text-nek-gray">{hub.complexText}</p>
            </div>
            <button type="button" onClick={() => openForm({ need: t.form.needs[12] })} className="btn-coral shrink-0" data-testid="hub-complex-cta">{t.cta.presentSituation}</button>
          </Reveal>
        </Section>
      )}

      {/* Risk note (epargne) */}
      {category === "epargne" && (
        <Section tone="white" className="!pb-0">
          <div className="rounded-xl border border-nek-border bg-nek-light p-5 text-sm text-nek-gray">{hub.riskNote}</div>
        </Section>
      )}

      <Section tone={category === "professionnels" ? "light" : "white"}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => <ProductCard key={s.slug} service={s} ctaLabel={category === "professionnels" ? t.cta.studyNeed : t.cta.discover} />)}
        </div>
      </Section>

      <FinalCta />
    </>
  );
}
